import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { C } from '../theme';
import planiqLogo from '../assets/logo.png';

const API = process.env.REACT_APP_API_URL || '';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(location.state?.openForgot ? 'forgot' : 'login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState(location.state?.forgotEmail || '');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();
  const successMessage = location.state?.message;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
    } catch (_) {}
    setForgotSent(true);
    setForgotLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      background: 'linear-gradient(135deg, #e8eef5 0%, #dce6f0 40%, #e4ecf5 70%, #d8e8f2 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative background shapes */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {/* Large cube bottom-left */}
        <div style={{ position: 'absolute', bottom: '8%', left: '8%', width: 120, height: 120, background: 'linear-gradient(135deg, #fff 60%, #c8d8e8)', borderRadius: 18, boxShadow: '8px 8px 32px rgba(100,140,180,0.18)', transform: 'rotate(-12deg)' }}/>
        {/* Small cubes floating */}
        <div style={{ position: 'absolute', bottom: '38%', left: '14%', width: 44, height: 44, background: 'linear-gradient(135deg, #e8f0f8, #d0e0ee)', borderRadius: 8, boxShadow: '4px 4px 16px rgba(100,140,180,0.15)', transform: 'rotate(-8deg)' }}/>
        <div style={{ position: 'absolute', bottom: '55%', left: '22%', width: 32, height: 32, background: 'linear-gradient(135deg, #f0f6fb, #dceaf5)', borderRadius: 6, boxShadow: '3px 3px 12px rgba(100,140,180,0.12)', transform: 'rotate(5deg)' }}/>
        <div style={{ position: 'absolute', bottom: '68%', left: '10%', width: 24, height: 24, background: 'linear-gradient(135deg, #e4eef8, #d4e4f0)', borderRadius: 5, boxShadow: '2px 2px 8px rgba(100,140,180,0.12)', transform: 'rotate(-15deg)' }}/>
        <div style={{ position: 'absolute', bottom: '72%', left: '28%', width: 18, height: 18, background: '#e8f2f8', borderRadius: 4, transform: 'rotate(10deg)' }}/>
        {/* Network dots right side */}
        {[[85,20],[92,35],[78,45],[88,58],[95,70],[80,72],[90,82]].map(([l,t],i)=>(
          <div key={i} style={{ position: 'absolute', left: l+'%', top: t+'%', width: 6, height: 6, borderRadius: '50%', background: 'rgba(150,180,210,0.5)' }}/>
        ))}
        {/* Connecting lines suggestion */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.25 }}>
          <polyline points="85,20 92,35 78,45 88,58 95,70 80,72 90,82" vectorEffect="non-scaling-stroke" fill="none" stroke="#90b8d8" strokeWidth="1" strokeDasharray="4,4"/>
          <line x1="78" y1="45" x2="95" y2="70" vectorEffect="non-scaling-stroke" stroke="#90b8d8" strokeWidth="0.8" strokeDasharray="3,5"/>
          <line x1="85" y1="20" x2="78" y2="45" vectorEffect="non-scaling-stroke" stroke="#90b8d8" strokeWidth="0.8"/>
          <line x1="88" y1="58" x2="80" y2="72" vectorEffect="non-scaling-stroke" stroke="#90b8d8" strokeWidth="0.8" strokeDasharray="3,4"/>
        </svg>
      </div>

      {/* Login card */}
      <div style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ marginBottom: 10 }}>
            <img src={planiqLogo} alt="PlanIQ" style={{ width: 240, height: 'auto' }} />
          </div>
          <div style={{ fontSize: 13, color: '#8aa0b8', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 10 }}>planiq.live</div>
          <div style={{ fontSize: 15, color: '#4a6a8a', fontWeight: 400, letterSpacing: '0.01em' }}>
            Where digital planning meets execution.
          </div>
        </div>

        {/* Form card */}
        <div style={{
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.9)',
          borderRadius: 16,
          padding: 36,
          boxShadow: '0 8px 40px rgba(80,120,160,0.12)',
        }}>
          {view === 'login' ? (
            <>
              {successMessage && <div style={{ ...C.feedback('success'), marginBottom: 16 }}>{successMessage}</div>}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', color: '#6a8aaa', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required
                    style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.8)', border: '1px solid #c8d8e8', borderRadius: 8, color: '#1a2a3a', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border .15s' }}
                    onFocus={e => e.target.style.borderColor = '#0090d0'}
                    onBlur={e => e.target.style.borderColor = '#c8d8e8'}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', color: '#6a8aaa', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                    style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.8)', border: '1px solid #c8d8e8', borderRadius: 8, color: '#1a2a3a', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border .15s' }}
                    onFocus={e => e.target.style.borderColor = '#0090d0'}
                    onBlur={e => e.target.style.borderColor = '#c8d8e8'}
                  />
                </div>
                <div style={{ textAlign: 'right', marginBottom: 20 }}>
                  <span onClick={() => { setView('forgot'); setError(''); }} style={{ fontSize: 12, color: '#0090d0', cursor: 'pointer', fontWeight: 500 }}>
                    Forgot password?
                  </span>
                </div>

                {error && <div style={C.feedback('danger')}>{error}</div>}

                <button type="submit" disabled={loading} style={{
                  width: '100%', padding: '12px',
                  background: loading ? '#c0d0e0' : 'linear-gradient(135deg, #0090d0, #0060a0)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 16px rgba(0,120,200,0.25)', transition: 'all .15s',
                }}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
              <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6a8aaa' }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: '#0090d0', fontWeight: 600, textDecoration: 'none' }}>Sign up</Link>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2a3a', marginBottom: 10 }}>Reset your password</div>
              {forgotSent ? (
                <div>
                  <div style={{ ...C.feedback('success'), marginBottom: 20 }}>
                    If an account exists with this email, you'll receive a reset link shortly.
                  </div>
                  <span onClick={() => { setView('login'); setForgotSent(false); setForgotEmail(''); }} style={{ fontSize: 13, color: '#0090d0', cursor: 'pointer', fontWeight: 600 }}>
                    Back to login
                  </span>
                </div>
              ) : (
                <form onSubmit={handleForgot}>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', color: '#6a8aaa', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Email address</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} autoFocus required
                      style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.8)', border: '1px solid #c8d8e8', borderRadius: 8, color: '#1a2a3a', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border .15s' }}
                      onFocus={e => e.target.style.borderColor = '#0090d0'}
                      onBlur={e => e.target.style.borderColor = '#c8d8e8'}
                    />
                  </div>
                  <button type="submit" disabled={forgotLoading} style={{
                    width: '100%', padding: '12px',
                    background: forgotLoading ? '#c0d0e0' : 'linear-gradient(135deg, #0090d0, #0060a0)',
                    border: 'none', borderRadius: 8, color: '#fff',
                    fontWeight: 700, fontSize: 14, cursor: forgotLoading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 16px rgba(0,120,200,0.25)', transition: 'all .15s', marginBottom: 16,
                  }}>
                    {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                  </button>
                  <div style={{ textAlign: 'center' }}>
                    <span onClick={() => setView('login')} style={{ fontSize: 13, color: '#0090d0', cursor: 'pointer', fontWeight: 600 }}>
                      Back to login
                    </span>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
